import { HttpClient, HttpEventType, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { DeliveryOrderStatusEnum } from '../enum/delivery-order-status.enum';
import {
  CancelDeliveryOrder,
  DeliveryOrder,
  DeliveryOrderExportPickingCriteria,
  DeliveryOrderHistory,
  DeliveryOrderPagination,
  DeliveryOrderPrintCriteria,
  DeliveryOrderSearchCriteria,
  ManualChangeStatus,
  RescheduleDeliveryOrder,
  SplitNoItem
} from '../models/delivery-order.model';
import { FileResponse } from '../models/fileResponse';
import { MetricActionType, MetricModule, MetricType, Metrics } from '../models/metrics';
import { Progress } from '../models/progress.model';
import { BaseService } from './base.service';

type UploadResultType = Progress | FileResponse | null | any;

@Injectable()
export class DeliveryOrderService extends BaseService {
  public headers: HttpHeaders;

  constructor(
    private readonly http: HttpClient,
    private readonly logger: NGXLogger
  ) {
    super();
    this.envService = this.env.services.deliveryOrder;
    this.headers = new HttpHeaders(this.envService.headers);
  }

  public getDeliveryOrderByDoNo(doNo: string): Observable<DeliveryOrder> {
    const headers: HttpHeaders = new HttpHeaders(this.env.services.deliveryOrder.headers);
    return this.http.get<any>(`${this.getUrl()}/${encodeURIComponent(doNo)}`, {
      headers,
      observe: 'body'
    });
  }

  public updateDeliveryOrder(request: DeliveryOrder): Observable<DeliveryOrder> {
    const items = this.prepareDeliverySplitNoData(request);
    const url = this.getFullUrl(this.envService.update, {
      id: request.id
    });

    return this.http.post<DeliveryOrder>(
      url,
      { items, version: request.version },
      {
        headers: this.loaderHeaders(3000),
        observe: 'body'
      }
    );
  }

  public submitDeliveryOrder(request: DeliveryOrder): Observable<DeliveryOrder> {
    if (request.doStatus === DeliveryOrderStatusEnum.DRAFT) {
      return this.submitSplitDO(request);
    } else {
      return this.submitDO(request);
    }
  }

  public searchByCriteria(criteria: DeliveryOrderSearchCriteria): Observable<DeliveryOrderPagination> {
    const metricsReq: Metrics = {
      metrics: [
        {
          metricType: MetricType.RESPONSE_TIME,
          module: MetricModule.ORDER,
          route: this.envService.url,
          action: MetricActionType.SEARCH
        }
      ]
    };
    const params = this.getParams(criteria, true);

    return this.http.get<DeliveryOrderPagination>(this.getUrl(), {
      headers: this.loaderHeaders(null, metricsReq),
      observe: 'body',
      params
    });
  }

  public cancelDeliveryOrder(request: CancelDeliveryOrder): Observable<DeliveryOrder> {
    const url = this.getFullUrl(this.envService.cancel);

    return this.http.put<DeliveryOrder>(url, request, {
      headers: this.loaderHeaders(3000),
      observe: 'body'
    });
  }

  public cancelDispatchDeliveryOrder(request: CancelDeliveryOrder): Observable<DeliveryOrder> {
    const url = this.getFullUrl(this.envService.cancelDispatch);

    return this.http.put<DeliveryOrder>(url, request, {
      headers: this.loaderHeaders(3000),
      observe: 'body'
    });
  }

  public exportPicking(criteria: DeliveryOrderExportPickingCriteria): Observable<any> {
    const params = this.getParams(criteria, true);
    const url = this.getFullUrl(this.envService.exportPicking);

    const metricsReq: Metrics = {
      metrics: [
        {
          metricType: MetricType.COUNTER,
          module: MetricModule.DELIVERY_ORDER,
          path: `${this.envService.url}${this.envService.exportPicking}`,
          action: MetricActionType.EXPORT
        },
        {
          metricType: MetricType.RESPONSE_TIME,
          module: MetricModule.DELIVERY_ORDER,
          route: `${this.envService.url}${this.envService.exportPicking}`,
          action: MetricActionType.EXPORT
        }
      ]
    };

    return this.http.get<any>(url, {
      headers: this.loaderHeaders(0, metricsReq),
      observe: 'body',
      params,
      responseType: 'blob' as 'json'
    });
  }

  public printPdfDeliveryOrder(criteria: DeliveryOrderPrintCriteria): Observable<any> {
    const url = this.getFullUrl(this.envService.print, { ...criteria });
    const body = new HttpParams();

    return this.http.post<any>(url, body, {
      headers: this.loaderHeaders(),
      observe: 'body',
      responseType: 'blob' as 'json'
    });
  }

  public getUrlImport() {
    return this.getFullUrl(this.envService.importPicking);
  }

  public uploadDeliveryOrderFiles(
    files: Array<File>,
    url: string,
    metricsReq: Metrics
  ): Array<Observable<UploadResultType>> {
    return files.map(file => this.uploadDeliveryOrderFile(file, url, metricsReq));
  }

  public uploadDeliveryOrderFile(file: File, url: string, metricsReq: Metrics): Observable<UploadResultType> {
    const formData = new FormData();

    formData.append('file', file, file.name);

    this.headers.append('Content-Type', 'multipart/form-data');

    return this.http
      .post<any>(url, formData, {
        reportProgress: true,
        observe: 'events',
        headers: this.loaderHeaders(0, metricsReq)
      })
      .pipe(
        map(event => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              const progress = Math.round((100 * event.loaded) / event.total);
              return { status: 'progress', message: progress };
            case HttpEventType.Response:
              return { validations: event.body, status: 'done' };
            default:
              const msg = `Unhandled event: ${HttpEventType[event.type]}`;
              this.logger.info(msg);
              return null;
          }
        }),
        catchError(err => {
          return of(err);
        })
      );
  }

  public rescheduleDate(request: RescheduleDeliveryOrder): Observable<DeliveryOrder> {
    const url = this.getFullUrl(this.envService.reschedule);

    return this.http.post<DeliveryOrder>(url, request, {
      headers: this.loaderHeaders(3000),
      observe: 'body'
    });
  }

  public getHistoryLogs(deliveryOrderHistory: DeliveryOrderHistory): Observable<any> {
    const url = this.getFullUrl(this.envService.history, {
      doNo: encodeURIComponent(deliveryOrderHistory.doNo)
    });
    return this.http.get<any>(url, { headers: this.headers, observe: 'body' });
  }

  private submitDO(request: DeliveryOrder): Observable<DeliveryOrder> {
    const url = this.getFullUrl(this.envService.edit, {
      id: request.id
    });

    return this.http.put<DeliveryOrder>(url, request, {
      headers: this.loaderHeaders(3000),
      observe: 'body'
    });
  }

  private submitSplitDO(request: DeliveryOrder): Observable<DeliveryOrder> {
    const items = this.prepareDeliverySplitNoData(request);
    const url = this.getFullUrl(this.envService.submit, {
      id: request.id
    });

    return this.http.post<DeliveryOrder>(
      url,
      { items, version: request.version },
      {
        headers: this.loaderHeaders(3000),
        observe: 'body'
      }
    );
  }

  private prepareDeliverySplitNoData(request: DeliveryOrder): SplitNoItem[] {
    return request.items.map(doItem => {
      return {
        itemNo: doItem.itemNo,
        splitNo: doItem.splitNo
      };
    });
  }

  public manualChangeDeliveryStatus(request: ManualChangeStatus): Observable<DeliveryOrder> {
    const url = this.getFullUrl(this.envService.delivered);

    return this.http.post<DeliveryOrder>(url, request, {
      headers: this.loaderHeaders(3000),
      observe: 'body'
    });
  }
}
